<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  source: {
    type: Object,
    default: null,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit', 'close']);

const { t } = useI18n();

const form = reactive({
  code: '',
  name: '',
  website: '',
  greeting: '',
  customSystemPrompt: '',
  tone: '',
  isActive: true,
});

const nameError = ref('');

const toneOptions = computed(() => [
  { value: '', label: t('SOURCE_MGMT.FORM.TONE.DEFAULT') },
  { value: 'neutral', label: t('SOURCE_MGMT.FORM.TONE.NEUTRAL') },
  { value: 'friendly', label: t('SOURCE_MGMT.FORM.TONE.FRIENDLY') },
  { value: 'formal', label: t('SOURCE_MGMT.FORM.TONE.FORMAL') },
]);

const isEditing = computed(() => !!props.source?.id);
const dialogTitle = computed(() =>
  isEditing.value ? t('SOURCE_MGMT.EDIT.TITLE') : t('SOURCE_MGMT.ADD.TITLE')
);

const setFormValues = () => {
  form.code = props.source?.code || '';
  form.name = props.source?.name || '';
  form.website = props.source?.website || '';
  form.greeting = props.source?.greeting || '';
  form.customSystemPrompt = props.source?.customSystemPrompt || '';
  form.tone = props.source?.tone || '';
  form.isActive = props.source ? !!props.source.isActive : true;
  nameError.value = '';
};

watch(() => props.source, setFormValues, { immediate: true });

const onClose = () => {
  emit('close');
};

const onSubmit = () => {
  const name = form.name.trim();
  if (!name) {
    nameError.value = t('SOURCE_MGMT.FORM.NAME.REQUIRED_ERROR');
    return;
  }
  emit('submit', {
    code: form.code.trim() || null,
    name,
    website: form.website.trim() || null,
    greeting: form.greeting || null,
    customSystemPrompt: form.customSystemPrompt || null,
    tone: form.tone || null,
    isActive: form.isActive,
  });
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="dialogTitle" />
    <form
      class="flex flex-col w-full gap-4 px-0 py-0"
      @submit.prevent="onSubmit"
    >
      <Input
        v-model="form.code"
        :label="$t('SOURCE_MGMT.FORM.CODE.LABEL')"
        :placeholder="$t('SOURCE_MGMT.FORM.CODE.PLACEHOLDER')"
      />
      <Input
        v-model="form.name"
        :label="$t('SOURCE_MGMT.FORM.NAME.LABEL')"
        :placeholder="$t('SOURCE_MGMT.FORM.NAME.PLACEHOLDER')"
        :message="nameError"
        :message-type="nameError ? 'error' : 'info'"
        @input="nameError = ''"
      />
      <Input
        v-model="form.website"
        :label="$t('SOURCE_MGMT.FORM.WEBSITE.LABEL')"
        :placeholder="$t('SOURCE_MGMT.FORM.WEBSITE.PLACEHOLDER')"
      />
      <TextArea
        v-model="form.greeting"
        :label="$t('SOURCE_MGMT.FORM.GREETING.LABEL')"
      />
      <TextArea
        v-model="form.customSystemPrompt"
        :label="$t('SOURCE_MGMT.FORM.PROMPT.LABEL')"
      />
      <div class="flex flex-col gap-1">
        <span class="text-heading-3 text-n-slate-12">
          {{ $t('SOURCE_MGMT.FORM.TONE.LABEL') }}
        </span>
        <Select v-model="form.tone" :options="toneOptions" />
      </div>
      <label class="flex items-center gap-2 cursor-pointer w-fit">
        <Switch v-model="form.isActive" />
        <span class="text-body-main text-n-slate-12">
          {{ $t('SOURCE_MGMT.FORM.ACTIVE.LABEL') }}
        </span>
      </label>
      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('SOURCE_MGMT.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="
            isEditing
              ? $t('SOURCE_MGMT.FORM.EDIT')
              : $t('SOURCE_MGMT.FORM.CREATE')
          "
          :is-loading="isSaving"
        />
      </div>
    </form>
  </div>
</template>
